import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { array, arrayOf, bool, func, number, string, shape } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import {
  TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
  txIsPaymentConfirmed,
  txIsAccepted,
  txIsCanceled,
  txIsDeclined,
  txIsEnquired,
  txIsPaymentExpired,
  txIsPaymentPending,
  txIsRequested,
  txConfirmedPayment,
  txIsAutoRecreatedPayment,
  txIsCreatedPayment,
  TRANSITION_OFFER_EXTEND_BOOKING_DIRECTLY,
  TRANSITION_OFFER_EXTEND_BOOKING,
  ACTION_CUSTOMER_EXTEND_OFFER,
  TRANSITION_DISPUTE_AFTER_REQUEST_EXTEND_BOOKING,
  TRANSITION_DISPUTE_AFTER_OFFERED_EXTEND_BOOKING,
  TRANSITION_DISPUTE,
  TRANSITION_MARK_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING,
  TRANSITION_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING,
  TRANSITION_MARK_RETURN_ITEM,
  TRANSITION_DECLINE_EXTEND_BOOKING,
  txExtendedBooking,
  txIsStartingReview,
  txIsTransferred,
  txIsOfferExtendBooking,
  txIsReviewed,
  txIsInFirstReview,
  txIsOffer,
  txIsOffered,
  txRequestBookingExtend,
  txIsDeclineExtendBooking,
  txIsDisputed,
  ACTION_MANUAL_RETRY_PAYMENT,
  ACTION_RETRY_WITH_CURRENT_CARD,
  txIsRescheduled,
  txIsRescheduleAccepted,
} from '../../util/customTransaction/mainRentalTransaction';
import {
  exTxIsOfferExtendBooking,
  TRANSITION_EXTEND_MARK_RETURN_ITEM,
  TRANSITION_EXTEND_RESCHEDULE,
  TRANSITION_EXTEND_DECLINE_EXTEND_BOOKING,
  TRANSITION_EXTEND_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING,
  getExtendTxCanReschedule,
  exTxIsDisputed,
  TRANSITION_EXTEND_OFFER_EXTEND_BOOKING_DIRECTLY,
  TRANSITION_EXTEND_OFFER_EXTEND_BOOKING,
} from '../../util/customTransaction/extendRentalTransaction';
import { propTypes, DATE_TYPE_DATE } from '../../util/types';
import {
  ensureListing,
  ensureTransaction,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { isMobileSafari } from '../../util/userAgent';
import { convertMoneyToNumber, formatMoney } from '../../util/currency';
import {
  AvatarLarge,
  BookingPanel,
  NamedLink,
  ReviewModal,
  UserDisplayName,
  ExtendRentalModal,
  RescheduleSaleModal,
  IconSpinner,
} from '../../components';
import { SendMessageForm, OfferForm } from '../../forms';
import config from '../../config';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import get from 'lodash/get';
import moment from 'moment';

// These are internal components that make this file more readable.
import AddressLinkMaybe from './AddressLinkMaybe';
import BreakdownMaybe from './BreakdownMaybe';
import DetailCardHeadingsMaybe from './DetailCardHeadingsMaybe';
import DetailCardImage from './DetailCardImage';
import ExtendOfferDetailsMaybe from './ExtendOfferDetailsMaybe';
import FeedSection from './FeedSection';
import SaleActionButtonsMaybe from './SaleActionButtonsMaybe';
import PanelHeading, {
  HEADING_ENQUIRED,
  HEADING_PAYMENT_EXPIRED,
  HEADING_PRE_CREATE_PAYMENT,
  HEADING_REQUESTED,
  HEADING_RESCHEDULED,
  HEADING_RESCHEDULE_ACCEPTED,
  HEADING_ACCEPTED,
  HEADING_DECLINED,
  HEADING_CANCELED,
  HEADING_DELIVERED,
  HEADING_EXTEND,
  HEADING_OFFER_EXTEND,
  HEADING_REQUEST_EXTEND,
  HEADING_DISPUTED,
} from './PanelHeading';

import css from './TransactionPanel.css';
import TransitActionButtonsMaybe, {
  getNextInteractableTransition,
  getExtendNextInteractableTransition,
} from './TransitActionButtonsMaybe';
import Accordion from '../Accordion/Accordion';
import BondSection from './BondSection';
import { types as sdkTypes } from '../../util/sdkLoader';
import { dateFromLocalToAPI, getDateForEmail } from '../../util/dates';
import { sendDisputeNotificationToAdmin } from '../../util/ses';
import { useDispatch, useSelector } from 'react-redux';
import { waitForAutoTransitionStartReviewFinish } from '../../containers/TransactionPage/TransactionPage.duck';
import ConfirmDisputeModal from '../ConfirmDisputeModal/ConfirmDisputeModal';
const { Money } = sdkTypes;

const createListingLink = (listingId, label, listingDeleted, searchParams = {}, className = '') => {
  if (!listingDeleted) {
    const params = { id: listingId, slug: createSlug(label) };
    const to = { search: stringify(searchParams) };
    return (
      <NamedLink className={className} name="ListingPage" params={params} to={to}>
        {label}
      </NamedLink>
    );
  } else {
    return <FormattedMessage id="TransactionPanel.deletedListingOrderTitle" />;
  }
};

// Helper function to get display names for different roles
const displayNames = (currentUser, currentProvider, currentCustomer, intl) => {
  const authorDisplayName = <UserDisplayName user={currentProvider} intl={intl} />;
  const customerDisplayName = <UserDisplayName user={currentCustomer} intl={intl} />;

  let otherUserDisplayName = '';
  let otherUserDisplayNameString = '';
  const currentUserIsCustomer =
    currentUser.id && currentCustomer.id && currentUser.id.uuid === currentCustomer.id.uuid;
  const currentUserIsProvider =
    currentUser.id && currentProvider.id && currentUser.id.uuid === currentProvider.id.uuid;

  if (currentUserIsCustomer) {
    otherUserDisplayName = authorDisplayName;
    otherUserDisplayNameString = userDisplayNameAsString(currentProvider, '');
  } else if (currentUserIsProvider) {
    otherUserDisplayName = customerDisplayName;
    otherUserDisplayNameString = userDisplayNameAsString(currentCustomer, '');
  }

  return {
    authorDisplayName,
    customerDisplayName,
    otherUserDisplayName,
    otherUserDisplayNameString,
  };
};

const TransactionPanelComponent = props => {
  const [sendMessageFormFocused, setSendMessageFormFocused] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isConfirmDisputeModalOpen, setIsConfirmDisputeModalOpen] = useState(false);

  const isMobSaf = useRef(false);
  const sendMessageFormName = useRef('TransactionPanel.SendMessageForm');
  const sendMessageButton = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    isMobSaf.current = isMobileSafari();
  }, []);

  useEffect(() => {
    const { location } = props;
    if (location) {
      const { isOpenDirectly = false } = parse(location.search);
      isOpenDirectly && setIsReviewModalOpen(true);
    }
  }, [props.location]);

  const onOpenConfirmDisputeModal = () => {
    setIsConfirmDisputeModalOpen(true);
  };

  const onCloseConfirmDisputeModal = () => {
    setIsConfirmDisputeModalOpen(false);
  };

  const onOpenReviewModal = () => {
    setIsReviewModalOpen(true);
  };

  const onSubmitReview = values => {
    const { onSendReview, transaction, transactionRole } = props;
    const currentTransaction = ensureTransaction(transaction);
    const { reviewRating, reviewContent } = values;
    const rating = Number.parseInt(reviewRating, 10);
    onSendReview(transactionRole, currentTransaction, rating, reviewContent)
      .then(r => {
        setIsReviewModalOpen(false);
        setReviewSubmitted(true);
      })
      .catch(e => {
        // Do nothing.
      });
  };

  const onSendMessageFormFocus = () => {
    setSendMessageFormFocused(true);
    if (isMobSaf.current) {
      // Scroll to bottom
      window.scroll({ top: document.body.scrollHeight, left: 0, behavior: 'smooth' });
    }
  };

  const onSendMessageFormBlur = () => {
    setSendMessageFormFocused(false);
  };

  const onMessageSubmit = (values, form) => {
    const message = values.message ? values.message.trim() : null;
    const { transaction, onSendMessage } = props;
    const ensuredTransaction = ensureTransaction(transaction);

    if (!message) {
      return;
    }
    onSendMessage(ensuredTransaction.id, message)
      .then(messageId => {
        form.reset();
        scrollToMessage(messageId);
      })
      .catch(e => {
        // Ignore, Redux handles the error
      });
  };

  const scrollToMessage = messageId => {
    const selector = `#msg-${messageId.uuid}`;
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  };

  const onSubmitExtendRental = async (values) => {
    if (sendMessageButton.current) {
      sendMessageButton.current.click();
    }

    const { onTransit, transaction, onCreateHoldingSlotTx } = props;
    const { bookingDates, price, transactionId, transition, seats } = values;
    const { startDate, endDate } = bookingDates;
    const bookingStart = dateFromLocalToAPI(startDate);
    const bookingEnd = dateFromLocalToAPI(endDate);

    const listing = ensureListing(transaction.listing);

    const holdingSlotTxParams = {
      listingId: listing.id,
      bookingStart,
      bookingEnd,
      seats,
    }
    try {
      const holdingSlotTxId = await onCreateHoldingSlotTx({
        txId: transactionId.uuid,
        params: holdingSlotTxParams,
      });

      const params = {
        protectedData: {
          holdingSlotTxId,
          extendBookingData: {
            startDate: startDate.getTime(),
            endDate: endDate.getTime(),
            seats,
            extendPrice: price
              ? {
                amount: price.amount,
                currency: price.currency,
              }
              : null,
            extendPriceForEmail: price
              ? {
                amount: convertMoneyToNumber(new Money(price.amount, price.currency)),
                currency: price.currency,
              }
              : null,
            endDateForEmail: getDateForEmail(endDate.getTime()),
          },
        },
      };

      await onTransit(transactionId, transition, params);
      setIsExtendModalOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const onDispute = ({ transaction, transitionName }) => {
    const { onTransit } = props;

    if (sendMessageButton.current) {
      sendMessageButton.current.click();
    }

    onTransit(transaction.id, transitionName)
      .then(() => {
        onCloseConfirmDisputeModal();
        sendDisputeNotificationToAdmin(transaction.id.uuid);
      })
      .catch(err => console.log(err));
  };

  const transitAndSendMessage = (transactionId, transition, params) => {
    const { onTransit } = props;

    if (sendMessageButton.current) {
      sendMessageButton.current.click();
    }
    return onTransit(transactionId, transition, params);
  };

  const onOpenRescheduleModal = () => {
    setIsRescheduleModalOpen(true);
  };

  const actionSale = (action, txId) => {
    if (sendMessageButton.current) {
      sendMessageButton.current.click();
    }
    action(txId);
  };

  const renewBondAndSendMessage = txId => {
    const { onRenewBond } = props;
    if (sendMessageButton.current) {
      sendMessageButton.current.click();
    }
    onRenewBond(txId);
  };

  const onTransitWithDeclineHoldingSlotTx = ({transaction, transitionName}) => {
    const { onTransit, onDeclineHoldingSlotTx } = props;

    onTransit(transaction.id, transitionName)
      .then(() => {
        onDeclineHoldingSlotTx(transaction.id.uuid);
      })
      .catch(err => console.log(err));
  }
  const onMarkReturnItem = ({ transaction, transitionName }) => {
    const { onTransit, onDeclineHoldingSlotTx } = props;

    if (sendMessageButton.current) {
      sendMessageButton.current.click();
    }

    const {
      start: bookingStart,
      end: bookingEnd,
      displayStart: bookingDisplayStart
    } = get(
      transaction,
      'booking.attributes',
      {}
    );
    const extendBookingData = get(transaction, 'atttributes.protectedData.extendBookingData', null);
    const oneDayAfterBookingStartMoment = moment(bookingStart).add(1, 'days');
    const bookingEndMoment = moment(bookingEnd);

    // CASE 1: item is returned within 3 days after bookingEnd
    // No need update booking
    if (moment(new Date()).diff(moment(bookingEnd), 'days') >= 0) {
      onTransit(transaction.id, transitionName)
        .then(() => {
          dispatch(waitForAutoTransitionStartReviewFinish(transaction.id));
        })
        .catch(err => console.log(err));
      return;
    }
    let newBookingEnd;
    // CASE 2: item is returned on the same date as bookingStart
    if (new Date(bookingStart).toDateString() === new Date().toDateString()) {
      newBookingEnd = moment()
        .add(1, 'days')
        .toDate();
    }
    // CASE 3: item is returned after bookingStart and before/on bookingEnd
    else if (moment().isBetween(oneDayAfterBookingStartMoment, bookingEndMoment)) {
      newBookingEnd = moment().toDate();
    }

    const params = {
      bookingStart,
      bookingEnd: newBookingEnd,
      bookingDisplayStart,
      bookingDisplayEnd: moment(newBookingEnd)
        .subtract(1, 'd')
        .endOf('day')
        .toDate(),
    };

    onTransit(transaction.id, transitionName, params)
      .then(() => {
        if(extendBookingData) {
          onDeclineHoldingSlotTx(transaction.id.uuid);
        }
        dispatch(waitForAutoTransitionStartReviewFinish(transaction.id));
      })
      .catch(err => console.log(err));
  };

  const {
    rootClassName,
    className,
    currentUser,
    transaction,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    initialMessageFailed,
    savePaymentMethodFailed,
    fetchMessagesInProgress,
    fetchMessagesError,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    onManageDisableScrolling,
    onShowMoreMessages,
    transactionRole,
    intl,
    onRescheduleSale,
    onAcceptSale,
    onDeclineSale,
    rescheduleInProgress,
    acceptInProgress,
    declineInProgress,
    rescheduleSaleError,
    acceptSaleError,
    declineSaleError,
    onSubmitBookingRequest,
    timeSlots,
    fetchTimeSlotsError,
    nextTransitions,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    transitInProgress,
    transitError,
    onFetchExtendTransactionLineItem,
    onCustomerExtendRental,
    extendTransactions,
    lastExTransaction,
    onTimeSlotsRequest,
    onManualRetry,
    retryPaymentInProgress,
    retryPaymentError,
    onRetryPayment,
    renewBondInProgress,
    renewBondError,
    onFetchReofferTransactionLineItem,
    onChangeAvailabilityExceptionCurrentBooking,
    exceptionId,
    speculateTransactionInProgress,
    onExtendRescheduleSale,
    initiateOrderInProgress,
  } = props;

  const extendTxCanReschedule = getExtendTxCanReschedule(extendTransactions);
  const waitAutoTransitionStartReviewInProgress = useSelector(
    state => state.TransactionPage.waitAutoTransitionStartReviewInProgress
  );
  const waitAutoTransitionStartReviewError = useSelector(
    state => state.TransactionPage.waitAutoTransitionStartReviewError
  );

  const currentTransaction = ensureTransaction(transaction);
  const currentListing = ensureListing(currentTransaction.listing);
  const currentProvider = ensureUser(currentTransaction.provider);
  const currentCustomer = ensureUser(currentTransaction.customer);
  const isCustomer = transactionRole === 'customer';
  const isProvider = transactionRole === 'provider';

  const listingLoaded = !!currentListing.id;
  const listingDeleted = listingLoaded && currentListing.attributes.deleted;
  const iscustomerLoaded = !!currentCustomer.id;
  const isCustomerBanned = iscustomerLoaded && currentCustomer.attributes.banned;
  const isCustomerDeleted = iscustomerLoaded && currentCustomer.attributes.deleted;
  const isProviderLoaded = !!currentProvider.id;
  const isProviderBanned = isProviderLoaded && currentProvider.attributes.banned;
  const isProviderDeleted = isProviderLoaded && currentProvider.attributes.deleted;

  const stateDataFn = tx => {
    if (txIsEnquired(tx)) {
      const transitions = Array.isArray(nextTransitions)
        ? nextTransitions.map(transition => {
          return transition.attributes.name;
        })
        : [];
      const hasCorrectNextTransition =
        transitions.length > 0 && transitions.includes(TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY);
      return {
        headingState: HEADING_ENQUIRED,
        showBookingPanel: isCustomer && !isProviderBanned && hasCorrectNextTransition,
        showOfferForm: isProvider && !isCustomerBanned,
      };
    } else if (txIsOffer(tx)) {
      return {
        headingState: HEADING_ENQUIRED,
        showBookingPanel: isCustomer && !isProviderBanned && txIsOffered(tx),
        showOfferForm: isProvider && !isCustomerBanned,
      };
    } else if (txIsPaymentPending(tx)) {
      return {
        headingState: HEADING_PRE_CREATE_PAYMENT,
        showDetailCardHeadings: true,
      };
    } else if (txIsPaymentExpired(tx)) {
      return {
        headingState: HEADING_PAYMENT_EXPIRED,
        showDetailCardHeadings: true,
      };
    } else if (txIsRequested(tx)) {
      return {
        headingState: HEADING_REQUESTED,
        showDetailCardHeadings: true,
        showSaleButtons: isProvider && !isCustomerBanned,
      };
    } else if (txIsRescheduled(tx)) {
      return {
        headingState: HEADING_RESCHEDULED,
        showDetailCardHeadings: true,
      };
    } else if (txIsRescheduleAccepted(tx)) {
      return {
        headingState: HEADING_RESCHEDULE_ACCEPTED,
        showDetailCardHeadings: true,
      };
    } else if (
      txIsAccepted(tx) ||
      txIsPaymentConfirmed(tx) ||
      txConfirmedPayment(tx) ||
      txIsAutoRecreatedPayment(tx) ||
      txIsCreatedPayment(tx) ||
      txIsTransferred(tx)
    ) {
      return {
        headingState: HEADING_ACCEPTED,
        showDetailCardHeadings: true,
      };
    } else if (txIsDisputed(tx) || exTxIsDisputed(tx)) {
      return {
        headingState: HEADING_DISPUTED,
        showDetailCardHeadings: true,
        showAddress: isCustomer,
      };
    } else if (txIsDeclined(tx)) {
      return {
        headingState: HEADING_DECLINED,
        showDetailCardHeadings: true,
      };
    } else if (txIsCanceled(tx)) {
      return {
        headingState: HEADING_CANCELED,
        showDetailCardHeadings: true,
      };
    } else if (txRequestBookingExtend(tx)) {
      return {
        headingState: HEADING_REQUEST_EXTEND,
        showDetailCardHeadings: true,
      };
    } else if (txIsOfferExtendBooking(tx)) {
      return {
        headingState: HEADING_OFFER_EXTEND,
        showDetailCardHeadings: true,
        showExtendOfferDetails: true,
      };
    } else if (txIsDeclineExtendBooking(tx)) {
      return {
        headingState: HEADING_OFFER_EXTEND,
        showDetailCardHeadings: true,
      };
    } else if (txExtendedBooking(tx)) {
      return {
        headingState: HEADING_EXTEND,
        showDetailCardHeadings: true,
      };
    } else if (txIsStartingReview(tx) || txIsReviewed(tx) || txIsInFirstReview(tx)) {
      return {
        headingState: HEADING_DELIVERED,
        showDetailCardHeadings: true,
        showAddress: isCustomer,
        hideTransitButton: true,
      };
    } else {
      return { headingState: 'unknown' };
    }
  };
  const stateData = stateDataFn(currentTransaction);

  const exStateDataFn = tx => {
    if (exTxIsOfferExtendBooking(tx)) {
      return {
        showExtendOfferDetails: isCustomer,
      };
    } else {
      return {};
    }
  };
  const exStateData = exStateDataFn(lastExTransaction);

  const deletedListingTitle = intl.formatMessage({
    id: 'TransactionPanel.deletedListingTitle',
  });

  const {
    authorDisplayName,
    customerDisplayName,
    otherUserDisplayName,
    otherUserDisplayNameString,
  } = displayNames(currentUser, currentProvider, currentCustomer, intl);

  const { publicData, geolocation } = currentListing.attributes;
  const location = publicData && publicData.location ? publicData.location : {};
  const listingTitle = currentListing.attributes.deleted
    ? deletedListingTitle
    : currentListing.attributes.title;

  const unitType = config.bookingUnitType;

  const { pricingType } = publicData || {};
  const priceDuration = config.custom.priceDurationConfig.find(ele => ele.key === pricingType);
  const unitTranslationKey = priceDuration
    ? priceDuration.labelListingId
    : 'TransactionPanel.perDay';

  const price = currentListing.attributes.price;
  const bookingSubTitle = price
    ? `${formatMoney(intl, price)} ${intl.formatMessage({ id: unitTranslationKey })}`
    : '';

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const saleButtons = (
    <SaleActionButtonsMaybe
      showButtons={stateData.showSaleButtons}
      rescheduleInProgress={rescheduleInProgress}
      acceptInProgress={acceptInProgress}
      declineInProgress={declineInProgress}
      rescheduleSaleError={rescheduleSaleError}
      acceptSaleError={acceptSaleError}
      declineSaleError={declineSaleError}
      onAcceptSale={() => actionSale(onAcceptSale, currentTransaction.id)}
      onDeclineSale={() => actionSale(onDeclineSale, currentTransaction.id)}
      onRescheduleSale={onOpenRescheduleModal}
    />
  );

  const nextInteractableTransitions = getNextInteractableTransition(currentTransaction, isProvider);

  const extendNextInteractableTransitions = getExtendNextInteractableTransition(
    lastExTransaction,
    isProvider
  );

  const mainTxHasNextTransit = nextInteractableTransitions.length > 0;
  const extendTxHasNextTransit = extendNextInteractableTransitions.length > 0;
  const haveNextTransit = mainTxHasNextTransit || extendTxHasNextTransit;

  const transitTransaction = extendTxHasNextTransit
    ? extendNextInteractableTransitions
    : nextInteractableTransitions;

  const actionTransaction = extendTxHasNextTransit ? lastExTransaction : currentTransaction;
  const transactionId = currentTransaction.id ? currentTransaction.id.uuid : null;

  const retryPaymentErrorMessage = retryPaymentError ? (
    <FormattedMessage id="TransactionPanel.retryPaymentError" />
  ) : null;
  const allTransitInProgress = initiateOrderInProgress ||
    transitInProgress ||
    retryPaymentInProgress ||
    speculateTransactionInProgress;
  const transitButtons = (
    <TransitActionButtonsMaybe
      transaction={actionTransaction}
      transitInProgress={allTransitInProgress}
      transitError={transitError}
      onTransit={transitAndSendMessage}
      transitions={transitTransaction}
      customActions={{
        [TRANSITION_OFFER_EXTEND_BOOKING_DIRECTLY]: () => setIsExtendModalOpen(true),
        [TRANSITION_OFFER_EXTEND_BOOKING]: () => setIsExtendModalOpen(true),
        [ACTION_CUSTOMER_EXTEND_OFFER]: onCustomerExtendRental,
        [TRANSITION_EXTEND_OFFER_EXTEND_BOOKING_DIRECTLY]: () => setIsExtendModalOpen(true),
        [TRANSITION_EXTEND_OFFER_EXTEND_BOOKING]: () => setIsExtendModalOpen(true),
        [TRANSITION_DISPUTE_AFTER_REQUEST_EXTEND_BOOKING]: onOpenConfirmDisputeModal,
        [TRANSITION_DISPUTE_AFTER_OFFERED_EXTEND_BOOKING]: onOpenConfirmDisputeModal,
        [TRANSITION_DISPUTE]: onOpenConfirmDisputeModal,
        [TRANSITION_MARK_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING]: onMarkReturnItem,
        [TRANSITION_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING]: onMarkReturnItem,
        [TRANSITION_MARK_RETURN_ITEM]: onMarkReturnItem,
        [TRANSITION_EXTEND_MARK_RETURN_ITEM]: onMarkReturnItem,
        [ACTION_MANUAL_RETRY_PAYMENT]: onManualRetry,
        [ACTION_RETRY_WITH_CURRENT_CARD]: () => {
          if (sendMessageButton.current) {
            sendMessageButton.current.click();
          }
          onRetryPayment(transactionId);
        },
        [TRANSITION_EXTEND_RESCHEDULE]: onOpenRescheduleModal,
        [TRANSITION_DECLINE_EXTEND_BOOKING]: onTransitWithDeclineHoldingSlotTx,
        [TRANSITION_EXTEND_DECLINE_EXTEND_BOOKING]: onTransitWithDeclineHoldingSlotTx,
        [TRANSITION_MARK_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING]:
          onTransitWithDeclineHoldingSlotTx,
        [TRANSITION_EXTEND_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING]:
          onTransitWithDeclineHoldingSlotTx,
      }}
      customErrorMessage={retryPaymentErrorMessage}
    />
  );

  const showSendMessageForm =
    !isCustomerBanned && !isCustomerDeleted && !isProviderBanned && !isProviderDeleted;

  const sendMessagePlaceholder = intl.formatMessage(
    { id: 'TransactionPanel.sendMessagePlaceholder' },
    { name: otherUserDisplayNameString }
  );

  const sendingMessageNotAllowed = intl.formatMessage({
    id: 'TransactionPanel.sendingMessageNotAllowed',
  });

  const paymentMethodsPageLink = (
    <NamedLink name="PaymentMethodsPage">
      <FormattedMessage id="TransactionPanel.paymentMethodsPageLink" />
    </NamedLink>
  );

  const mainLabel = intl.formatMessage({ id: 'TransactionPanel.mainLabel' });

  const classes = classNames(rootClassName || css.root, className);

  const showBookingBreakdown =
    currentTransaction &&
    currentTransaction.id &&
    currentTransaction.booking &&
    currentTransaction.booking.id;

  const listingLink = createListingLink(
    currentListing.id && currentListing.id.uuid,
    listingTitle,
    listingDeleted
  );

  const waitToStartReviewMessage = waitAutoTransitionStartReviewInProgress ? (
    <div className={css.waitAutoTransitionMessage}>
      <IconSpinner className={css.waitAutoTransitionSpinner} />
      <FormattedMessage id="TransactionPanel.waitAutoTransitionStartReviewInProgressMessage" />
    </div>
  ) : waitAutoTransitionStartReviewError ? (
    <div className={classNames(css.waitAutoTransitionMessage, css.error)}>
      <FormattedMessage id="TransactionPanel.waitAutoTransitionStartReviewErrorMessage" />
    </div>
  ) : null;

  return (
    <div className={classes}>
      <div className={css.container}>
        <div className={css.txInfo}>
          <DetailCardImage
            rootClassName={css.imageWrapperMobile}
            avatarWrapperClassName={css.avatarWrapperMobile}
            listingTitle={listingTitle}
            image={firstImage}
            provider={currentProvider}
            isCustomer={isCustomer}
          />
          {isProvider ? (
            <div className={css.avatarWrapperProviderDesktop}>
              <AvatarLarge user={currentCustomer} className={css.avatarDesktop} />
            </div>
          ) : null}

          <PanelHeading
            panelHeadingState={stateData.headingState}
            transactionRole={transactionRole}
            providerName={authorDisplayName}
            customerName={customerDisplayName}
            isCustomerBanned={isCustomerBanned}
            listingId={currentListing.id && currentListing.id.uuid}
            listingTitle={listingTitle}
            listingDeleted={listingDeleted}
          />

          <BondSection
            className={css.bondMobile}
            intl={intl}
            currentTransaction={currentTransaction}
            isCustomer={isCustomer}
            onRenewBond={renewBondAndSendMessage}
            renewBondInProgress={renewBondInProgress}
            renewBondError={renewBondError}
          />

          {(stateData.showExtendOfferDetails && isCustomer) ||
          exStateData.showExtendOfferDetails ? (
            <ExtendOfferDetailsMaybe
              className={css.extendOfferDetailsContainerMobile}
              userRole="customer"
              unitType={config.bookingUnitType}
              transaction={actionTransaction}
              dateType={DATE_TYPE_DATE}
            />
          ) : null}

          <div className={css.bookingDetailsMobile}>
            <h2 className={css.detailCardTitleMobile}>{listingLink}</h2>

            <AddressLinkMaybe
              rootClassName={css.addressMobile}
              location={location}
              geolocation={geolocation}
              showAddress={stateData.showAddress}
            />

            <Accordion>
              {showBookingBreakdown ? (
                <BreakdownMaybe
                  transaction={currentTransaction}
                  transactionRole={transactionRole}
                  show
                  label={mainLabel}
                  isBondFeeVisible={true}
                />
              ) : null}
              {extendTransactions.map((transaction, index) => {
                const label = intl.formatMessage(
                  { id: 'TransactionPanel.extendLabel' },
                  { index: index + 1 }
                );
                return (
                  <BreakdownMaybe
                    key={index}
                    transaction={transaction}
                    transactionRole={transactionRole}
                    label={label}
                  />
                );
              })}
            </Accordion>
          </div>

          {savePaymentMethodFailed ? (
            <p className={css.genericError}>
              <FormattedMessage
                id="TransactionPanel.savePaymentMethodFailed"
                values={{ paymentMethodsPageLink }}
              />
            </p>
          ) : null}
          <FeedSection
            rootClassName={css.feedContainer}
            currentTransaction={currentTransaction}
            currentUser={currentUser}
            fetchMessagesError={fetchMessagesError}
            fetchMessagesInProgress={fetchMessagesInProgress}
            initialMessageFailed={initialMessageFailed}
            messages={messages}
            oldestMessagePageFetched={oldestMessagePageFetched}
            onOpenReviewModal={onOpenReviewModal}
            onShowMoreMessages={() => onShowMoreMessages(currentTransaction.id)}
            totalMessagePages={totalMessagePages}
            extendTransactions={extendTransactions}
          />

          {waitToStartReviewMessage}

          {showSendMessageForm ? (
            <SendMessageForm
              formId={sendMessageFormName}
              rootClassName={css.sendMessageForm}
              messagePlaceholder={sendMessagePlaceholder}
              inProgress={sendMessageInProgress}
              sendMessageError={sendMessageError}
              onFocus={onSendMessageFormFocus}
              onBlur={onSendMessageFormBlur}
              onSubmit={onMessageSubmit}
              sendMessageButton={sendMessageButton}
            />
          ) : (
            <div className={css.sendingMessageNotAllowed}>{sendingMessageNotAllowed}</div>
          )}

          {stateData.showSaleButtons ? (
            <div className={css.mobileActionButtons}>{saleButtons}</div>
          ) : null}
          {haveNextTransit && !stateData.hideTransitButton ? (
            <div className={css.mobileActionButtons}>{transitButtons}</div>
          ) : null}
        </div>

        <div className={css.asideDesktop}>
          <div className={css.detailCard}>
            <DetailCardImage
              avatarWrapperClassName={css.avatarWrapperDesktop}
              listingTitle={listingTitle}
              image={firstImage}
              provider={currentProvider}
              isCustomer={isCustomer}
            />

            <DetailCardHeadingsMaybe
              showDetailCardHeadings={stateData.showDetailCardHeadings}
              listingTitle={listingLink}
              subTitle={bookingSubTitle}
              location={location}
              geolocation={geolocation}
              showAddress={stateData.showAddress}
            />

            <BondSection
              className={css.accordionDesktop}
              intl={intl}
              currentTransaction={currentTransaction}
              isCustomer={isCustomer}
              onRenewBond={renewBondAndSendMessage}
              renewBondInProgress={renewBondInProgress}
              renewBondError={renewBondError}
            />

            {(stateData.showExtendOfferDetails && isCustomer) ||
            exStateData.showExtendOfferDetails ? (
              <ExtendOfferDetailsMaybe
                className={css.extendOfferDetailsContainerDesktop}
                userRole="customer"
                unitType={config.bookingUnitType}
                transaction={actionTransaction}
                dateType={DATE_TYPE_DATE}
              />
            ) : null}

            {stateData.showBookingPanel ? (
              <BookingPanel
                className={css.bookingPanel}
                titleClassName={css.bookingTitle}
                isOwnListing={false}
                listing={currentListing}
                title={listingTitle}
                subTitle={bookingSubTitle}
                authorDisplayName={authorDisplayName}
                onSubmit={onSubmitBookingRequest}
                onManageDisableScrolling={onManageDisableScrolling}
                timeSlots={timeSlots}
                fetchTimeSlotsError={fetchTimeSlotsError}
                onFetchTransactionLineItems={onFetchTransactionLineItems}
                lineItems={lineItems}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                fetchLineItemsError={fetchLineItemsError}
                transaction={currentTransaction}
                onTransit={transitAndSendMessage}
                transitInProgress={transitInProgress}
                transitError={transitError}
                onTimeSlotsRequest={onTimeSlotsRequest}
              />
            ) : null}

            {stateData.showOfferForm ? (
              <OfferForm
                className={css.bookingPanel}
                transaction={currentTransaction}
                isProvider={isProvider}
                isCustomer={isCustomer}
                transitInProgress={transitInProgress}
                transitError={transitError}
                onTransit={transitAndSendMessage}
                unitType={unitType}
                timeSlots={timeSlots}
                fetchTimeSlotsError={fetchTimeSlotsError}
                onSubmit={onSubmitBookingRequest}
                onManageDisableScrolling={onManageDisableScrolling}
                listing={currentListing}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                onTimeSlotsRequest={onTimeSlotsRequest}
              />
            ) : null}

            <Accordion className={css.accordionDesktop}>
              {showBookingBreakdown ? (
                <BreakdownMaybe
                  className={css.breakdownContainer}
                  transaction={currentTransaction}
                  transactionRole={transactionRole}
                  show
                  label={mainLabel}
                />
              ) : null}

              {extendTransactions.map((transaction, index) => {
                const label = intl.formatMessage(
                  { id: 'TransactionPanel.extendLabel' },
                  { index: index + 1 }
                );
                return (
                  <BreakdownMaybe
                    key={index}
                    className={css.breakdownContainer}
                    transaction={transaction}
                    transactionRole={transactionRole}
                    label={label}
                    isBondFeeVisible={false}
                  />
                );
              })}
            </Accordion>

            {stateData.showSaleButtons ? (
              <div className={css.desktopActionButtons}>{saleButtons}</div>
            ) : null}
            {haveNextTransit && !stateData.hideTransitButton ? (
              <div className={css.desktopActionButtons}>{transitButtons}</div>
            ) : null}
          </div>
        </div>
      </div>
      <ReviewModal
        id="ReviewOrderModal"
        isOpen={isReviewModalOpen}
        onCloseModal={() => setIsReviewModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        onSubmitReview={onSubmitReview}
        revieweeName={otherUserDisplayName}
        reviewSent={reviewSubmitted}
        sendReviewInProgress={sendReviewInProgress}
        sendReviewError={sendReviewError}
      />
      <ExtendRentalModal
        id="ExtendRentalModal"
        isOpen={isExtendModalOpen}
        isCustomer={isCustomer}
        isProvider={isProvider}
        transaction={actionTransaction}
        onCloseModal={() => setIsExtendModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        onSubmitExtendRental={onSubmitExtendRental}
        extendInProgress={transitInProgress}
        extendError={transitError}
        customerDisplayName={customerDisplayName}
        listingTitle={listingTitle}
        isOwnListing={true}
        listing={currentListing}
        timeSlots={timeSlots}
        fetchTimeSlotsError={fetchTimeSlotsError}
        onFetchExtendTransactionLineItem={onFetchExtendTransactionLineItem}
        lineItems={lineItems}
        fetchLineItemsInProgress={fetchLineItemsInProgress}
        fetchLineItemsError={fetchLineItemsError}
      />
      <ConfirmDisputeModal
        id="ConfirmDisputeModal"
        isOpen={isConfirmDisputeModalOpen}
        onClose={onCloseConfirmDisputeModal}
        onManageDisableScrolling={onManageDisableScrolling}
        disputeInProgress={transitInProgress}
        disputeError={transitError}
        onDispute={onDispute}
        transaction={actionTransaction}

      />
      {stateData.showSaleButtons || haveNextTransit ? (
        <RescheduleSaleModal
          id="RescheduleSaleModal"
          isOpen={isRescheduleModalOpen}
          transaction={currentTransaction}
          onRescheduleSale={onRescheduleSale}
          onCloseModal={() => setIsRescheduleModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          rescheduleInProgress={rescheduleInProgress}
          rescheduleSaleError={rescheduleSaleError}
          listing={currentListing}
          isOwnListing={true}
          timeSlots={timeSlots}
          fetchTimeSlotsError={fetchTimeSlotsError}
          onFetchReofferTransactionLineItem={onFetchReofferTransactionLineItem}
          lineItems={lineItems}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchLineItemsError={fetchLineItemsError}
          sendMessageButton={sendMessageButton}
          onChangeAvailabilityExceptionCurrentBooking={onChangeAvailabilityExceptionCurrentBooking}
          exceptionId={exceptionId}
          onExtendRescheduleSale={onExtendRescheduleSale}
          extendTxCanReschedule={extendTxCanReschedule}
        />
      ) : null}
    </div>
  );
};

TransactionPanelComponent.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  acceptSaleError: null,
  declineSaleError: null,
  fetchMessagesError: null,
  initialMessageFailed: false,
  savePaymentMethodFailed: false,
  sendMessageError: null,
  sendReviewError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  nextTransitions: null,
  lineItems: null,
  fetchLineItemsError: null,
};

TransactionPanelComponent.propTypes = {
  rootClassName: string,
  className: string,

  currentUser: propTypes.currentUser,
  transaction: propTypes.transaction.isRequired,
  totalMessagePages: number.isRequired,
  oldestMessagePageFetched: number.isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  initialMessageFailed: bool,
  savePaymentMethodFailed: bool,
  fetchMessagesInProgress: bool.isRequired,
  fetchMessagesError: propTypes.error,
  sendMessageInProgress: bool.isRequired,
  sendMessageError: propTypes.error,
  sendReviewInProgress: bool.isRequired,
  sendReviewError: propTypes.error,
  onManageDisableScrolling: func.isRequired,
  onShowMoreMessages: func.isRequired,
  onSendMessage: func.isRequired,
  onSendReview: func.isRequired,
  onSubmitBookingRequest: func.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,
  nextTransitions: array,
  onTimeSlotsRequest: func.isRequired,

  // Sale related props
  onRescheduleSale: func.isRequired,
  onAcceptSale: func.isRequired,
  onDeclineSale: func.isRequired,
  rescheduleInProgress: bool.isRequired,
  acceptInProgress: bool.isRequired,
  declineInProgress: bool.isRequired,
  rescheduleSaleError: propTypes.error,
  acceptSaleError: propTypes.error,
  declineSaleError: propTypes.error,

  // line items
  onFetchTransactionLineItems: func.isRequired,
  onFetchExtendTransactionLineItem: func.isRequired,
  onFetchReofferTransactionLineItem: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape,
  location: shape({
    search: string,
  }),
  onExtendRescheduleSale: func.isRequired,
};

const TransactionPanel = compose(injectIntl, withRouter)(TransactionPanelComponent);

export default TransactionPanel;
